import { ArrayUtils, MGPValidation, MGPOptional, Utils } from '@everyboard/lib';
import { GameNode } from '../../jscaip/AI/GameNode';
import { Coord } from '../../jscaip/Coord';
import { GameStatus } from '../../jscaip/GameStatus';
import { PlayerOrNone } from '../../jscaip/Player';
import { Rules } from '../../jscaip/Rules';
import { RulesFailure } from '../../jscaip/RulesFailure';
import { TableUtils } from '../../jscaip/TableUtils';
import { DiamFailure } from './DiamFailure';
import { DiamMoveShift } from './DiamMove';
import { DiamPiece } from './DiamPiece';
import { DiamState } from './DiamState';
export class DiamNode extends GameNode {
}
export class DiamRules extends Rules {
    static singleton = MGPOptional.empty();
    static get() {
        if (DiamRules.singleton.isAbsent()) {
            DiamRules.singleton = MGPOptional.of(new DiamRules());
        }
        return DiamRules.singleton.get();
    }
    getInitialState() {
        const _ = DiamPiece.EMPTY;
        const board = [
            [_, _, _, _, _, _, _, _],
            [_, _, _, _, _, _, _, _],
            [_, _, _, _, _, _, _, _],
            [_, _, _, _, _, _, _, _],
        ];
        return new DiamState(board, [4, 4, 4, 4], 0);
    }
    applyLegalMove(move, state, _config, _info) {
        if (move.isDrop()) {
            return this.applyLegalDrop(move, state);
        }
        else {
            return this.applyLegalShift(move, state);
        }
    }
    applyLegalDrop(drop, state) {
        const newBoard = TableUtils.copy(state.board);
        newBoard[state.getStackHeight(drop.target)][drop.target] = drop.piece;
        const newRemainingPieces = ArrayUtils.copy(state.remainingPieces);
        newRemainingPieces[DiamState.pieceIndex(drop.piece)] -= 1;
        return new DiamState(newBoard, newRemainingPieces, state.turn + 1);
    }
    applyLegalShift(shift, state) {
        const newBoard = TableUtils.copy(state.board);
        const targetX = shift.getTarget();
        let targetY = state.getStackHeight(targetX);
        let sourceY = shift.start.y;
        while (sourceY < DiamState.HEIGHT && state.getPieceAtXY(shift.start.x, sourceY) !== DiamPiece.EMPTY) {
            newBoard[targetY][targetX] = state.getPieceAtXY(shift.start.x, sourceY);
            newBoard[sourceY][shift.start.x] = DiamPiece.EMPTY;
            targetY++;
            sourceY++;
        }
        return new DiamState(newBoard, state.remainingPieces, state.turn + 1);
    }
    isLegal(move, state) {
        if (move.isDrop()) {
            return this.isDropLegal(move, state);
        }
        else {
            return this.isShiftLegal(move, state);
        }
    }
    isDropLegal(drop, state) {
        Utils.assert(drop.target < DiamState.WIDTH, 'DiamMoveDrop out of board');
        Utils.assert(drop.piece.owner !== PlayerOrNone.NONE, 'DiamMoveDrop cannot contain an empty piece');
        if (drop.piece.owner === state.getCurrentOpponent()) {
            return MGPValidation.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_OPPONENT());
        }
        if (state.getRemainingPiecesOf(drop.piece) === 0) {
            return MGPValidation.failure(DiamFailure.NO_MORE_PIECES_OF_THIS_TYPE());
        }
        return this.dropHeightValidity(drop, state);
    }
    dropHeightValidity(drop, state) {
        if (state.getStackHeight(drop.target) === DiamState.HEIGHT) {
            return MGPValidation.failure(DiamFailure.SPACE_IS_FULL());
        }
        return MGPValidation.SUCCESS;
    }
    isShiftLegal(shift, state) {
        const piece = state.getPieceAt(shift.start);
        if (piece.owner.isNone()) {
            return MGPValidation.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_EMPTY());
        }
        if (piece.owner === state.getCurrentOpponent()) {
            return MGPValidation.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_OPPONENT());
        }
        return this.shiftHeightValidity(shift, state);
    }
    shiftHeightValidity(shift, state) {
        const movedHeight = state.getStackHeight(shift.start.x) - shift.start.y;
        const resultingHeight = state.getStackHeight(shift.getTarget()) + movedHeight;
        if (resultingHeight > DiamState.HEIGHT) {
            return MGPValidation.failure(DiamFailure.TARGET_STACK_TOO_HIGH());
        }
        return MGPValidation.SUCCESS;
    }
    getGameStatus(node) {
        const highestAlignment = this.findHighestAlignment(node.gameState);
        if (highestAlignment.isPresent()) {
            const winningPiece = node.gameState.getPieceAt(highestAlignment.get());
            Utils.assert(winningPiece.owner.isPlayer(), 'highest alignment is owned by a player');
            return GameStatus.getVictory(winningPiece.owner);
        }
        else {
            return GameStatus.ONGOING;
        }
    }
    findHighestAlignment(state) {
        for (let x = 0; x < DiamState.WIDTH / 2; x++) {
            for (let y = DiamState.HEIGHT - 1; y > 0; y--) { // skip 0 because ground alignment isn't a win
                const pieceHere = state.getPieceAtXY(x, y);
                const pieceThere = state.getPieceAtXY(x + (DiamState.WIDTH / 2), y);
                if (pieceHere !== DiamPiece.EMPTY && pieceHere === pieceThere) {
                    return MGPOptional.of(new Coord(x, y));
                }
            }
        }
        return MGPOptional.empty();
    }
    pieceCanMove(state, coord) {
        if (this.isShiftLegal(new DiamMoveShift(coord, 'clockwise'), state).isSuccess()) {
            return true;
        }
        else if (this.isShiftLegal(new DiamMoveShift(coord, 'counterclockwise'), state).isSuccess()) {
            return true;
        }
        else {
            return false;
        }
    }
}
